/**
 * Pure decision for the Report Builder's fit-to-width auto-zoom.
 *
 * The editor auto-fits the A4 page to the column width by measuring the pane and
 * calling the fork's imperative `setZoom`, re-run on a ResizeObserver so the fit
 * stays correct when the Notes / QA columns toggle. But that observer also fires
 * on the reflow the user's OWN zoom triggers, which kept snapping their zoom back
 * to the fit value (MDRS-402). So we back off once the user takes manual control:
 * if the current zoom no longer matches the value we last auto-applied, the user
 * changed it via the toolbar — leave it alone.
 */

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.2;
// A4 portrait (~794px) plus the editor's margin/chrome ≈ 860px effective width;
// 0.95 keeps a borderline width off the horizontal scrollbar.
const TARGET_WIDTH = 860;
const FIT_FACTOR = 0.95;
// Float slack: `getZoom()` can round our value a hair. Anything within this of
// our last auto value is still "ours", not a manual change.
const MANUAL_EPSILON = 0.001;

/**
 * The zoom to apply to fit `clientWidth`, or `null` to leave the zoom untouched
 * because the user has manually zoomed away from our last auto value.
 *
 * @param clientWidth  current width of the editor pane
 * @param currentZoom  the editor's current zoom (`getZoom()`)
 * @param lastAutoZoom the zoom we last auto-applied, or `null` on the first pass
 */
export function computeFitZoom(
  clientWidth: number,
  currentZoom: number,
  lastAutoZoom: number | null
): number | null {
  if (
    lastAutoZoom !== null &&
    Math.abs(currentZoom - lastAutoZoom) > MANUAL_EPSILON
  ) {
    return null; // user took manual control — respect it
  }
  const target = (clientWidth / TARGET_WIDTH) * FIT_FACTOR;
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, target));
}
